/**
 *
 * Revenue metrics calculation utils
 * Like ADR, Revpar, occupancy, budget variance, ...
 *
 */
import { get, set } from 'lodash';
import { BUDGET_SUB_KEYS } from './types';
import { roundPercentage } from './numbers';

/**
 * REVENUE MANAGEMENT METRICS
 */

/**
 * [Internal] Calculates the ADR (Average Daily Rate)
 *
 * @example calculateAdr(100, 5) // returns 20
 * @example calculateAdr(100, 0) // returns 0
 *
 * @param  {Number} revenueTotal - the total amount of revenue generated during the period, in cents
 * @param  {Number} roomsSoldTotal - the number of rooms sold during the period
 * @return {Number} the ADR, rounded, or 0 if roomsSoldTotal = 0
 */
const calculateAdr = (revenueTotal, roomsSoldTotal) => {
    const result = roomsSoldTotal > 0 ? revenueTotal / roomsSoldTotal : 0;
    return result;
};

/**
 * [Internal] Calculates the RevPar (Revenue per Available Room)
 *
 * @example calculateRevPar(100, 4) // returns 25
 * @example calculateRevPar(100, 0) // returns 0
 *
 * @param  {Number} revenueTotal - the total amount of revenue generated during the period, in cents
 * @param  {Number} availableRoomCount - the number of rooms available during the period
 * @return {Number} the RevPar, rounded, or 0 if availableRoomCount = 0
 */
const calculateRevPar = (revenueTotal, availableRoomCount) => {
    const result = availableRoomCount > 0 ? revenueTotal / availableRoomCount : 0;
    return result;
};

/**
 * [Internal] Calculates the Occupancy (% of rooms occupied)
 *
 * @example calculateOccupancy(50, 100) // returns 0.5 (50%)
 * @example calculateOccupancy(14, 58) // returns 0.2414 (24.14%)
 * @example calculateOccupancy(75, 0) // returns 0
 *
 * @param  {Number} roomsSoldTotal - the number of rooms sold during the period
 * @param  {Number} availableRoomCount - the number of rooms available during the period
 * @return {Number} the occupancy, rounded to 4 decimals, or 0 if availableRoomCount = 0
 */
const calculateOccupancy = (roomsSoldTotal, availableRoomCount) => {
    const result = availableRoomCount > 0 ? roomsSoldTotal / availableRoomCount : 0;
    return roundPercentage(result);
};

/**
 * Calculates the ADR, RevPar & Occupancy figures
 *
 * @example
 * // returns { adr: 15000, revpAR: 11250, occupancy: 0.75 }
 * calculateMetrics(100, 75, 1125000)
 *
 * @param  {Number} availableRoomCount - number of rooms available in the property
 * @param  {Number} rooms - number of rooms sold
 * @param  {Number} revenue - revenue total
 * @return {Object} an object with the calculated adr/revPar/occupancy
 */
const calculateMetrics = (availableRoomCount, rooms, revenue, forecast) => {
    let forecastData;
    if (forecast) {
        forecastData = {
            rooms: forecast.rooms,
            revenue: forecast.revenue,
            occupancy: calculateOccupancy(forecast.rooms, availableRoomCount),
            adr: calculateAdr(forecast.revenue, forecast.rooms),
            revPar: calculateRevPar(forecast.revenue, availableRoomCount),
        };
    }
    return {
        adr: calculateAdr(revenue, rooms),
        revPar: calculateRevPar(revenue, availableRoomCount),
        occupancy: calculateOccupancy(rooms, availableRoomCount),
        forecast: forecastData,
    };
};

/**
 * BUDGET VARIANCE
 */

/**
 * Calculate the variance: difference between budget & actual figures
 * @param  {Object}   budgetFigures - the budget figures object (see keys for format)
 * @param  {Object}   actualFigures - the obejct with actual revenue figures (see keys for format)
 * @param  {String[]} keys - list of keys to calculate variance for (defaults to BUDGET_SUB_KEYS)
 * @return {Object}   an object with the same format as defined by the keys, with calculated variances
 */
const calculateBudgetVariances = (budgetFigures, actualFigures, keys = BUDGET_SUB_KEYS) =>
    keys.reduce((accu, key) => {
        const budget = get(budgetFigures, key, null);
        const actual = get(actualFigures, key, 0);
        const variance = budget === null ? null : actual - budget;
        return set({ ...accu }, key, variance);
    }, {});

export { calculateAdr, calculateRevPar, calculateOccupancy, calculateMetrics, calculateBudgetVariances };
